import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import Layout from 'src/components/common/Layout'
import SEO from 'src/components/common/SEO'
import { attachCssVariables } from 'src/modules/design-system/styles/utils'
import { channels, fanduelChannel } from 'src/constants/channelStoreData'
import { useCheckFanduelRSN } from 'src/modules/hooks/useCheckFanduelRSN'
import { addParams } from 'src/modules/utils/addParams'
import HeroSection from './components/HeroSection'
import ChannelDetailsSection from './components/ChannelDetailsSection'
import AllChannelsFooter from './components/AllChannelsFooter'
import FAQSection from 'src/components/common/FAQSectionNew'

const CHANNEL_STORE_FAQS = [
  {
    question: 'What is the Fubo Channel Store?',
    answer:
      "The Fubo Channel Store is the place to go to shop for TV's top channels. You can subscribe to individual news, sports and entertainment channels without the need for a Fubo base plan, which means you can watch more of the TV and sports you love, and pay less.",
  },
  {
    question: "How do I watch channels I've subscribed to through Fubo?",
    answer:
      'When you subscribe to a channel through Fubo, you can watch it in the free Fubo app. You may also be able to enjoy Fubo features including Unlimited Cloud DVR, simultaneous streams and Multiview.',
  },
  {
    question: 'What is FOX Nation and how much does it cost?',
    answer:
      'FOX Nation offers 180+ shows and over 1,000 hours of original and exclusive content. It costs $8.99 per month.',
  },
  {
    question: 'Does FOX Nation offer a free trial through Fubo?',
    answer: 'Yes. You can start watching FOX Nation for free with no commitment.',
  },
  {
    question: 'What does a subscription to NBA TV include?',
    answer:
      'NBA TV includes live NBA games, behind-the-scenes content and exclusive access to top players and teams. A subscription is just $8.99 per month.',
  },
  {
    question: 'Can I try NBA TV for free on Fubo?',
    answer: 'Yes. A trial is available for you to try NBA TV for free before subscribing.',
  },
  {
    question: 'What content is available on beIN SPORTS?',
    answer:
      'beIN SPORTS offers 10 channels of world-class soccer including Ligue 1, SüperLig, Coupe de France, Copa Libertadores, Copa Sudamericana and more for only $5.99 per month.',
  },
  {
    question: 'How do I get access to beIN SPORTS through Fubo?',
    answer:
      'You can subscribe to beIN SPORTS by clicking the Get started button and checking out. Then, just download the Fubo app, log in with your Fubo account, and start streaming.',
  },
  {
    question: 'What does Paramount+ with SHOWTIME include?',
    answer:
      'For $10.99 per month, Paramount+ with SHOWTIME provides 9 channels of original series, blockbuster movies, boxing and full access to the SHOWTIME on-demand library.',
  },
  {
    question: 'What does DAZN1 offer?',
    answer:
      'DAZN1 costs $29.99 per month and offers action-packed fight nights, X Series crossover boxing, mixed martial arts and much more.',
  },
  {
    question: 'What do I get with a STARZ subscription?',
    answer:
      'STARZ offers access to all STARZ live channels plus a full on-demand library of shows and movies for only $10.99 per month.',
  },
  {
    question: 'What content is available with MGM+ and Hallmark+?',
    answer:
      'MGM+ ($6.99/mo) includes 3 channels of original series and blockbuster movies. Hallmark+ ($7.99/mo) features exclusive Hallmark original series, movies and feel-good entertainment.',
  },
]

const ChannelStore = ({
  lang = 'en',
  market = 'us',
  pageTitle,
  pageDescription,
  customChannels,
  location,
}) => {
  const { showFDPlanCards, rsnData } = useCheckFanduelRSN()

  const channelData = useMemo(() => {
    const baseChannels = customChannels || channels

    // Only add FanDuel if available in user's region and has offer code
    if (showFDPlanCards && rsnData?.offerCode) {
      return [
        ...baseChannels,
        {
          ...fanduelChannel,
          ctaLink: addParams('https://www.fubo.tv/signup', `offer=${rsnData.offerCode}`),
        },
      ]
    }

    return baseChannels
  }, [customChannels, showFDPlanCards, rsnData])

  useEffect(() => {
    attachCssVariables()
  }, [])

  return (
    <Layout lang={lang} market={market} location={location} noFooter>
      <SEO
        lang={lang}
        market={market}
        title={pageTitle || 'Channel Store - Watch more. Pay less. | Fubo'}
        description={
          pageDescription ||
          'Access individual channels without a Fubo base plan. Subscribe to Paramount+, DAZN1, STARZ, MGM+, Hallmark+, and FanDuel separately.'
        }
      />
      <StyledChannelStore>
        <HeroSection lang={lang} market={market} channels={channelData} />
        <ChannelDetailsSection lang={lang} market={market} channels={channelData} />
        <AllChannelsFooter lang={lang} market={market} channels={channelData} />
        <FAQSection lang={lang} market={market} isDarkMode faqListOverride={CHANNEL_STORE_FAQS} />
      </StyledChannelStore>
    </Layout>
  )
}

export default ChannelStore

const StyledChannelStore = styled.div`
  width: 100%;
  background: #000000;
  color: #ffffff;
`
